import type {
  AdvancedKeywordValue,
  PropConfigValue,
} from '@components/trackingPlans/createTrackingPlans/types';
import type { PaginatedList } from '@components/common/pagination/paginatedListStore';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { DefinitionPropertyStore } from './stores/definitionsStore/definitionProperty';
import type { VariantConfig } from '@components/trackingPlans/types';
import { BlockListResponse } from '@api';

export interface DataCatalogTrackingPlanApiResponse {
  id: string;
  name: string;
  events: {
    id: string;
    name: string;
  }[];
  properties: {
    id: string;
    name: string;
    required?: boolean;
    propertyKey?: string;
  }[];
}

export enum EventType {
  track = 'track',
  identify = 'identify',
  page = 'page',
  screen = 'screen',
  group = 'group',
}
export interface DataCatalogEventApiResponse {
  id: string;
  name: string;
  description?: string;
  categoryId?: string;
  updatedAt?: string;
  eventType: EventType;
  trackingPlans?: DataCatalogTrackingPlanApiResponse[];
  blockingLists?: Pick<BlockListResponse, 'resourceType' | 'resourceId' | 'enabled'>[];
}

export interface EventMetricsResponse {
  count: number;
  lastSent: string;
  name: string;
  type: string;
}

export type DataCatalogEventListApiResponse = PaginatedList<
  Omit<DataCatalogEventApiResponse, 'trackingPlans'> & {
    eventToTrackingPlans?: { trackingPlan: DataCatalogTrackingPlanApiResponse }[];
  }
>;

export type DataCatalogEventMetrics = {
  lastSeen: string;
  count: number;
};
export interface DataCatalogEvent extends Omit<DataCatalogEventApiResponse, 'trackingPlans'> {
  metrics?: DataCatalogEventMetrics;
  trackingPlans: DataCatalogEventTrackingPlan[];
}

export interface DataCatalogEventInput extends Omit<DataCatalogEventApiResponse, 'id'> {
  id?: string;
  blockLists?: DataCatalogEventApiResponse['blockingLists'];
}

export enum DataCatalogEventBlockedState {
  BLOCKED = 'blocked',
  NOT_BLOCKED = 'not_blocked',
  BLOCK_LISTED = 'block-listed',
}

export type DataCatalogEventTrackingPlan = DataCatalogTrackingPlanApiResponse & {
  connectedSources: {
    id: string;
    name: string;
    sourceDef: {
      name: string;
      displayName: string;
    };
  }[];
};

export type EventCreateInput = {
  name: string;
  description?: string;
  categoryId?: string;
  eventType?: EventType;
};

export type EventUpdateInput = Partial<EventCreateInput>;

export interface EventCategory {
  id: string;
  name: string;
  wasCreatedFromTemplate?: boolean;
  isTemplate?: boolean;
}
export type EventCategoryResponse = PaginatedList<EventCategory & { workspaceId?: string | null }>;

export interface EventsFilters {
  search?: string;
  types?: string[];
  categoryIds?: string[];
  trackingPlanIds?: string[];
}

export type PropertiesFilters = {
  search?: string;
  trackingPlanIds?: string[];
  types?: string[];
};

export type PropertyDataType =
  | 'string'
  | 'number'
  | 'integer'
  | 'object'
  | 'array'
  | 'boolean'
  | 'null';

export type PropertyType = PropertyDataType | string;

export interface DataCatalogPropertyApiResponse {
  id: string;
  name: string;
  description?: string;
  type: PropertyType[];
  arrayItemTypes?: PropertyType[];
  itemDefinition?: DefinitionApiItem;
  updatedAt?: string;
  propConfig?: PropConfigValue;
  definition?: DefinitionApiItem;
  definitionId?: string;
  itemDefinitionId?: string;
  trackingPlans?: DataCatalogTrackingPlanApiResponse[];
}

export type DataCatalogPropertyListApiResponse = PaginatedList<
  Omit<DataCatalogPropertyApiResponse, 'trackingPlans'> & {
    propToTrackingPlans?: DataCatalogTrackingPlanApiResponse[];
  }
>;

export type DataCatalogPropertyInput = Omit<DataCatalogPropertyApiResponse, 'id'> & {
  id?: string;
  type: string | PropertyType[];
  required?: boolean;
};

export interface DataCatalogProperty extends Omit<DataCatalogPropertyApiResponse, 'trackingPlans'> {
  properties?: (Omit<DataCatalogPropertyApiResponse, 'trackingPlans'> & { required?: boolean })[];
  additionalProperties?: boolean;
  trackingPlans: DataCatalogEventTrackingPlan[];
}

export type PropertyCreateInput = {
  name: string;
  description?: string;
  type?: PropertyType[];
  properties?: PropertyCreateInput[];
  propConfig?: PropConfigValue;
  definition?: DefinitionApiItem;
  itemDefinition?: DefinitionApiItem;
};

export type PropertyUpdateInput = Partial<PropertyCreateInput>;

export interface TypeOption {
  value: PropertyType;
  label: string;
  icon?: IconDefinition;
  disabled?: boolean;
  arrayLabel: string;
  disabledTooltip?: string;
}

// Definitions

export type UnionType = 'oneOf' | 'allOf' | 'anyOf';
export type DefinitionType = PropertyType | UnionType;
export type DefinitionPropertyInput = DataCatalogPropertyInput & { definitionId?: string };
export type DefinitionProperty = Omit<DataCatalogProperty, 'trackingPlans' | 'id'> & {
  id?: string;
  definitionId?: string;
  required?: boolean;
};
export interface DefinitionConfig extends AdvancedKeywordValue {
  itemTypes?: (PropertyType | 'custom')[];
}
export interface DefinitionApiItem {
  id: string;
  name: string;
  description?: string;
  updatedAt?: string;
  type: DefinitionType;
  dataType: PropertyType[];
  config?: DefinitionConfig;
  defs?: DefinitionApiItem[];
  itemDefinitions?: DefinitionApiItem[];
  properties?: DefinitionProperty[];
  variantProperties?: DefinitionProperty[];
  variants?: VariantConfig[];
}

export type DefinitionApiResponse = PaginatedList<DefinitionApiItem>;

export type DefinitionInput = Omit<DefinitionApiItem, 'definitionId'>;

export type DefinitionCreateInput = Omit<
  DefinitionApiItem,
  'id' | 'updatedAt' | 'properties' | 'defs'
> & {
  properties: DefinitionPropertyStore[];
  defs: DefinitionApiItem[];
};

export type DefinitionUpdateInput = Partial<DefinitionCreateInput>;

export type DefinitionFilters = {
  search?: string;
  types?: string[];
  names?: string[];
  ids?: string;
};
